import { Link } from '@tanstack/react-router'
import { ArrowRight, CheckCircle2, Monitor, QrCode } from 'lucide-react'
import { useMemo } from 'react'
import { EmptyState } from '@/components/empty-state'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useDisplayPage } from '@/features/displays/api'
import { HealthDot } from '@/features/displays/components/health-dot'
import { usePermission } from '@/hooks/use-permission'
import { formatRelative, humanize } from '@/lib/format'
import { usePairingDialog } from '@/stores/pairing'
import type { Display } from '@/types/api'

const MAX = 5

/** Orders problem displays: errors, then warnings, then offline (longest offline first). */
export function pickUnhealthy(warned: Display[], offline: Display[], max = MAX): Display[] {
  const byId = new Map<string, Display>()
  for (const d of [...warned, ...offline]) if (!byId.has(d.id)) byId.set(d.id, d)
  const score = (d: Display) => (d.health === 'error' ? 0 : d.warnings.length > 0 || d.health === 'warning' ? 1 : 2)
  return [...byId.values()].sort((a, b) => score(a) - score(b)).slice(0, max)
}

function reason(d: Display): string {
  if (d.connectivity === 'offline') return d.last_heartbeat_at ? `Offline since ${formatRelative(d.last_heartbeat_at)}` : 'Offline'
  const first = d.warnings[0]
  if (first) return d.warnings.length > 1 ? `${humanize(first.code)} +${d.warnings.length - 1} more` : humanize(first.code)
  return d.health === 'error' ? 'Reporting errors' : 'Needs attention'
}

export function DeviceHealthCard({ totalDisplays }: { totalDisplays: number | undefined }) {
  const canView = usePermission('displays.view')
  const canPair = usePermission('players.manage')
  const show = usePairingDialog((s) => s.show)
  const enabled = canView && (totalDisplays ?? 0) > 0
  const warned = useDisplayPage({ has_warnings: true, per_page: MAX }, enabled)
  const offline = useDisplayPage({ connectivity: 'offline', per_page: MAX, sort: '-last_heartbeat_at' }, enabled)
  const rows = useMemo(() => pickUnhealthy(warned.data ?? [], offline.data ?? []), [warned.data, offline.data])
  if (!canView) return null
  const loading = totalDisplays === undefined || (enabled && (warned.isPending || offline.isPending))

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle>Device health</CardTitle>
        {enabled && (
          <Button variant="ghost" size="sm" asChild>
            <Link to="/displays">
              All displays <ArrowRight />
            </Link>
          </Button>
        )}
      </CardHeader>
      <CardContent className="pb-3">
        {loading ? (
          <div className="space-y-3 pb-2">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : totalDisplays === 0 ? (
          <EmptyState
            compact
            icon={Monitor}
            title="No displays yet"
            description="Pair your first player to see live health here."
            action={
              canPair && (
                <Button size="sm" onClick={() => show()}>
                  <QrCode /> Pair player
                </Button>
              )
            }
          />
        ) : rows.length === 0 ? (
          <div className="flex items-center gap-3 rounded-lg bg-success/8 px-3 py-3 text-sm">
            <CheckCircle2 className="size-5 text-success" />
            <span className="font-medium">All displays healthy</span>
          </div>
        ) : (
          <ul className="-mx-2">
            {rows.map((d) => (
              <li key={d.id}>
                <Link to="/displays/$displayId" params={{ displayId: d.id }} className="flex items-center gap-3 rounded-lg px-2 py-2 outline-none hover:bg-muted/60 focus-visible:bg-muted/60">
                  <HealthDot display={d} pulse={false} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{d.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{reason(d)}</span>
                  </span>
                  {d.location && <span className="hidden max-w-[7rem] truncate text-xs text-muted-foreground sm:block">{d.location.name}</span>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
